import { collectionOptions } from "@tanstack/db";
import {
  hashKey,
  type QueryClient,
  type QueryFunctionContext,
} from "@tanstack/query-core";
import {
  createCursorPager,
  parseLoadSubsetOptions,
  queryCollectionOptions,
} from "@tanstack/query-db-collection";
import { apiGet } from "@softmax/observatory-client/client";
import type {
  Schemas,
  V2ExperienceRequestPage,
} from "@softmax/observatory-client/types";
import { requestIsActive } from "@softmax/web-features/research/records";
import {
  type ExperienceRequestFilters,
  type RequestPageScope,
  type RequestPageInfo,
} from "./request.types";

function experienceRequestSearchParams(
  filters: ExperienceRequestFilters,
): URLSearchParams {
  const query = new URLSearchParams({ mine: String(filters.mine) });

  if (filters.coworldId) query.set("coworld_id", filters.coworldId);

  if (filters.requesterUserId)
    query.set("requester_user_id", filters.requesterUserId);

  if (filters.leagueId) query.set("league_id", filters.leagueId);

  return query;
}

function fetchExperienceRequestPage({
  filters,
  selection,
  cursor,
  limit,
  elevatedPrivileges,
  signal,
}: RequestPageScope & {
  readonly signal: AbortSignal;
}): Promise<V2ExperienceRequestPage> {
  const query = experienceRequestSearchParams(filters);
  query.set("limit", String(limit));

  if (selection.event) query.set("event", selection.event);

  if (selection.start) query.set("start", selection.start);

  if (selection.end) query.set("end", selection.end);

  if (selection.status) query.set("status", selection.status);

  if (cursor) query.set("cursor", cursor);

  return apiGet<V2ExperienceRequestPage>(`/v2/experience-requests?${query}`, {
    signal,
    elevatedPrivileges,
  });
}

export function experienceRequestsPageCollections(scope: RequestPageScope) {
  const queryKey = ["experience-requests", "list", scope] as const;
  const identity = hashKey(queryKey);
  const queryFn = ({ signal }: QueryFunctionContext) =>
    fetchExperienceRequestPage({ ...scope, signal });
  return {
    requests: collectionOptions(`${identity}:requests`, (client) =>
      queryCollectionOptions<Schemas["V2ExperienceRequestRow"], typeof queryFn>(
        {
          queryClient: client.requireDependency<QueryClient>("queryClient"),
          queryKey,
          queryFn,
          select: (response) => response.entries,
          getKey: (row) => row.id,
          staleTime: 15_000,
          refetchInterval: (query) =>
            scope.pollActive &&
            query.state.data?.entries.some((row) => requestIsActive(row.status))
              ? 5_000
              : false,
        },
      ),
    ),
    page: collectionOptions(`${identity}:page`, (client) =>
      queryCollectionOptions<RequestPageInfo, typeof queryFn>({
        queryClient: client.requireDependency<QueryClient>("queryClient"),
        queryKey,
        queryFn,
        select: (response) => [
          { id: "page", nextCursor: response.next_cursor },
        ],
        getKey: (row) => row.id,
        staleTime: 15_000,
      }),
    ),
  };
}

export function experienceRequestHistoryCollection({
  filters,
  elevatedPrivileges,
}: {
  readonly filters: ExperienceRequestFilters;
  readonly elevatedPrivileges: boolean;
}) {
  const scope = { filters, elevatedPrivileges };
  const queryKey = ["experience-requests", "history", "rows", scope] as const;
  return collectionOptions(hashKey(queryKey), (client) => {
    const queryClient = client.requireDependency<QueryClient>("queryClient");
    const pager = createCursorPager({
      queryClient,
      queryKey: ["experience-requests", "history", "pages", scope],
      staleTime: 5_000,
      fetchPage: async (cursor, signal) => {
        const page = await fetchExperienceRequestPage({
          filters,
          selection: {},
          cursor: cursor ?? null,
          limit: 25,
          elevatedPrivileges,
          signal,
        });
        return { rows: page.entries, nextCursor: page.next_cursor };
      },
    });
    return queryCollectionOptions({
      queryClient,
      queryKey,
      syncMode: "on-demand",
      queryFn: ({ signal, meta }) =>
        pager.read(parseLoadSubsetOptions(meta?.loadSubsetOptions), signal),
      getKey: (row) => row.id,
      staleTime: 5_000,
      refetchInterval: (query) =>
        query.state.data?.some((row) => requestIsActive(row.status))
          ? 5_000
          : false,
    });
  });
}
